// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mergeDisplayMessages.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";

import { type AskV2ThreadMessage } from "./types";

function overlayUserComposerContext(
  history: AskPunttMessage,
  transportMessages: AskV2ThreadMessage[],
): AskPunttMessage {
  if (history.role !== "user") return history;
  const hasComment = (history.commentContexts?.length ?? 0) > 0;
  const hasQuoted = (history.quotedContexts?.length ?? 0) > 0;
  if (hasComment && hasQuoted) return history;

  const match = transportMessages.find((message) => {
    if (message.role !== "user") return false;
    if (message.serverId && message.serverId === history._id) return true;
    return message.clientId === history._id;
  });
  // why: history can land the just-sent user turn under its server _id before
  // ACK sets serverId on the optimistic row. Without a content fallback the
  // chips on that local row never copy onto the history row that replaces it.
  const fallback =
    match ??
    [...transportMessages]
      .reverse()
      .find(
        (message) =>
          message.role === "user" &&
          !message.serverId &&
          message.content === history.content &&
          ((message.commentContexts?.length ?? 0) > 0 ||
            (message.quotedContexts?.length ?? 0) > 0),
      );
  if (!fallback) return history;
  return {
    ...history,
    commentContexts: hasComment
      ? history.commentContexts
      : fallback.commentContexts,
    quotedContexts: hasQuoted
      ? history.quotedContexts
      : fallback.quotedContexts,
  };
}

function transportToAskPunttMessage(
  message: AskV2ThreadMessage,
  chatId: string,
): AskPunttMessage {
  const pending =
    message.status === "sending" || message.status === "streaming";
  return {
    _id: message.serverId ?? message.clientId,
    chatId,
    role: message.role,
    content: message.content,
    status: pending ? "pending" : "completed",
    createdAt: message.createdAt ?? "",
    userId: message.userId,
    attachments: message.attachments,
    quotedContexts: message.quotedContexts,
    commentContexts: message.commentContexts,
    mentions: message.mentions,
    mentionEmails: message.mentionEmails,
  };
}

/**
 * History rows are the display source of truth (full AskPunttMessage).
 * Optimistic transport rows (sending / failed / in-flight assistant)
 * append only when they are not already in history.
 */
export function mergeDisplayMessages(input: {
  historyMessages: AskPunttMessage[];
  transportMessages: AskV2ThreadMessage[];
  chatId: string;
}): AskPunttMessage[] {
  const historyIds = new Set(
    input.historyMessages.flatMap((message) =>
      [message._id].filter((id) => id.length > 0),
    ),
  );

  // why: history polling can persist and return the just-sent user turn (keyed
  // by its server _id) before the send ACK sets serverId on the optimistic row.
  // Without a content fallback the row lacks a matching id in either direction
  // and renders as a duplicate user bubble until the ACK lands. Guard against
  // that race by suppressing a still-unacked user row whose content already
  // appears as the newest history message.
  const historyMessages = input.historyMessages.map((message) =>
    overlayUserComposerContext(message, input.transportMessages),
  );
  const lastHistory = historyMessages[historyMessages.length - 1];

  const optimistic = input.transportMessages
    .filter((message) => {
      const serverId = message.serverId;
      const clientId = message.clientId;
      if (serverId && historyIds.has(serverId)) return false;
      if (historyIds.has(clientId)) return false;
      if (
        !serverId &&
        message.role === "user" &&
        lastHistory?.role === "user" &&
        lastHistory.content === message.content
      ) {
        return false;
      }
      // why: a live streaming ACK usually omits the assistant serverId, so the
      // in-flight assistant row carries only a local clientId. Once history
      // persists that turn (its own server _id != our clientId) both rows would
      // render and the answer bubble duplicates. Drop the local row only when
      // history's tail assistant is the same turn (content match), not when an
      // older assistant reply is still last in history while a new stream starts.
      if (
        !serverId &&
        message.role === "assistant" &&
        lastHistory?.role === "assistant" &&
        lastHistory.content === message.content
      ) {
        return false;
      }
      return true;
    })
    .map((message) => transportToAskPunttMessage(message, input.chatId));

  return [...historyMessages, ...optimistic];
}
